import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIError } from "../../constants/types";
import { ERROR_MESSAGES } from "../../constants/enums";
import { api } from "../../api/api";
import { IChatMessage, IHistoryContentPart, IHistoryItem, ISession, ISessionsApiResponse } from "./aiChat.types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Converts the raw API history item into the app's IChatMessage format.
 * Handles both:
 *  - user messages: context.content is a plain string
 *  - assistant messages: context.content is an array of { text, type } parts
 */
const mapHistoryItem = (item: IHistoryItem): IChatMessage => {
  const ctx = item.context;
  let content = "";

  if (typeof ctx.content === "string") {
    content = ctx.content;
  } else if (Array.isArray(ctx.content)) {
    content = (ctx.content as IHistoryContentPart[])
      .filter((p) => p.type === "output_text")
      .map((p) => p.text)
      .join("\n");
  }

  return {
    id: ctx.id,
    role: ctx.role === "assistant" ? "ai" : ctx.role,
    content,
    timestamp: item.insert_ts
      ? new Date(item.insert_ts).getTime()
      : Date.now(),
  };
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

/**
 * Fetches a page of chat history.
 * Returns messages in chronological order (oldest first).
 * The API returns newest-first, so we reverse the array.
 */
export const fetchChatHistory = createAsyncThunk<
  { messages: IChatMessage[]; hasMore: boolean; page: number },
  { pageNo: number; itemPerPage?: number; sessionId?: string },
  { rejectValue: APIError }
>(
  "aiChat/fetchChatHistory",
  async ({ pageNo, itemPerPage = 10, sessionId }: { pageNo: number; itemPerPage?: number; sessionId?: string }, { rejectWithValue }) => {
    try {
      let url = `/a2a/chat?pageNo=${pageNo}&itemPerPage=${itemPerPage}`;
      if (sessionId) {
        url += `&session_id=${sessionId}`;
      }
      const res = await api.get(url);
      const items: IHistoryItem[] = Array.isArray(res.data)
        ? res.data
        : (res.data?.data ?? []);

      const messages = items.map(mapHistoryItem).reverse();

      return {
        messages,
        hasMore: items.length >= itemPerPage,
        page: pageNo,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

/**
 * Sends a new user message to the AI.
 * On success, the streaming response arrives via socket events, so this
 * thunk only initiates the request. Returns the session_id from the response
 * so the reducer can track the active session.
 */
export const sendChatMessage = createAsyncThunk<
  { session_id: string },
  { message: string; pendingId: string; sessionId?: string },
  { rejectValue: APIError & { pendingId?: string }; state: { aiChat: { activeSessionId: string | null } } }
>(
  "aiChat/sendChatMessage",
  async ({ message, pendingId, sessionId }, { rejectWithValue, getState }) => {
    try {
      // Always prefer Redux state for the latest session_id (handles re-render delays)
      const stateSessionId = getState().aiChat.activeSessionId;
      const effectiveSessionId = stateSessionId || sessionId || null;
      const body: { message: string; session_id?: string } = { message };
      if (effectiveSessionId) {
        body.session_id = effectiveSessionId;
      }
      const response = await api.post("/a2a/chat", body);
      // Handle both { session_id: "..." } and { data: { session_id: "..." } } response shapes
      const resSessionId = response.data?.session_id || response.data?.data?.session_id;
      return { session_id: resSessionId };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
        pendingId,
      });
    }
  },
);

/**
 * Fetches a paginated list of chat sessions.
 * Returns sessions in the order provided by the API (newest first).
 */
export const fetchSessions = createAsyncThunk<
  { sessions: ISession[]; totalRecords: number; page: number },
  { pageNo: number; itemPerPage?: number },
  { rejectValue: APIError }
>(
  "aiChat/fetchSessions",
  async ({ pageNo, itemPerPage = 20 }: { pageNo: number; itemPerPage?: number }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/a2a/sessions?pageNo=${pageNo}&itemPerPage=${itemPerPage}`,
      );
      const responseData: ISessionsApiResponse = res.data;

      return {
        sessions: responseData.data,
        totalRecords: responseData.totalRecords,
        page: pageNo,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);
